import React, { useEffect, useMemo, useState } from "react";

import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import MasterV2 from "../MasterV2";

import {
  LoadingDiv,
  SweetAlertShow,
} from "../../master/Master";

import { courseList } from "../../../api/Courses";
import { batchList } from "../../../api/Batch";
import { StaffList } from "../../../api/Staff";
import {
  studentAttendanceList,
  studentAttendanceSendMessage,
  studentAttendancePresentSendMessage,
  studentAttendanceAbsentSendMessage,
  studentAttendanceFailedSendMessage,
  studentAttendancePresentFailedSendMessage,
  studentAttendanceAbsentFailedSendMessage,
} from "../../../api/Attendance";

import { formatToTime } from "../../master/FormatToDateTime";

/* =========================================================
   CONSTANTS
========================================================= */

const BATCH_NOT_SELECTED = "";
const ALL_BATCHES = "__all__";

/* =========================================================
   DATE HELPERS
========================================================= */

const getToday = () => {
  const date = new Date();

  return `${date.getFullYear()}-${String(
    date.getMonth() + 1
  ).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
};

const formatDateReadable = (value) => {
  if (!value) return "-";

  const date = new Date(`${value}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const safeTime = (value) => {
  if (!value) return "-";

  try {
    return formatToTime(value);
  } catch {
    return String(value);
  }
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getInitials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) =>
      word.charAt(0).toUpperCase()
    )
    .join("");

/* =========================================================
   MESSAGE STATUS
   DB convention:
   1 = sent
   2 = not sent
   3 = processing
   4 = failed
========================================================= */

const getMessageStatus = (value) => {
  const status = Number(value);

  if (status === 1) {
    return {
      key: "sent",
      label: "Delivered",
      shortLabel: "Sent",
      className: "delivery-sent",
      icon: "ri-checkbox-circle-line",
    };
  }

  if (status === 3) {
    return {
      key: "processing",
      label: "Processing",
      shortLabel: "Processing",
      className: "delivery-processing",
      icon: "ri-loader-4-line",
    };
  }

  if (status === 4) {
    return {
      key: "failed",
      label: "Failed",
      shortLabel: "Failed",
      className: "delivery-failed",
      icon: "ri-error-warning-line",
    };
  }

  return {
    key: "not_sent",
    label: "Not Delivered",
    shortLabel: "Not Sent",
    className: "delivery-not-sent",
    icon: "ri-close-circle-line",
  };
};

const createChannel = (
  key,
  label,
  icon,
  value
) => {
  const raw = Number(value || 2);
  const status = getMessageStatus(raw);

  return {
    key,
    label,
    icon,
    raw,
    ...status,
    delivered: raw === 1,
    processing: raw === 3,
    failed: raw === 4,
  };
};

const createMessageEvent = ({
  key,
  label,
  time,
  metaStatus,
  pushStatus,
}) => {
  const meta = createChannel(
    "meta",
    "WhatsApp / Meta",
    "ri-whatsapp-line",
    metaStatus
  );

  const push = createChannel(
    "push",
    "Push Notification",
    "ri-notification-3-line",
    pushStatus
  );

  const deliveredChannels = [
    meta,
    push,
  ].filter((channel) => channel.delivered);

  const hasDelivered =
    deliveredChannels.length > 0;

  const hasProcessing =
    meta.processing || push.processing;

  const hasFailed =
    meta.failed || push.failed;

  return {
    key,
    label,
    time,
    meta,
    push,
    deliveredChannels,
    hasDelivered,
    hasProcessing,
    hasFailed,
    overall: hasDelivered
      ? getMessageStatus(1)
      : hasProcessing
        ? getMessageStatus(3)
        : hasFailed
          ? getMessageStatus(4)
          : getMessageStatus(2),
  };
};

/* =========================================================
   COMPONENT
========================================================= */

export default function StudentAttendanceAndMessagesReport() {
  const [loading, setLoading] =
    useState(false);

  const [filterLoading, setFilterLoading] =
    useState(false);

  const [courses, setCourses] =
    useState([]);

  const [batches, setBatches] =
    useState([]);

  const [staffs, setStaffs] =
    useState([]);

  const [sessions, setSessions] =
    useState([]);

  const [data, setData] =
    useState({});

  const [search, setSearch] =
    useState("");

  const [deliveryFilter, setDeliveryFilter] =
    useState("all");

  const [messageType, setMessageType] = useState("both");
  const [sendingMessages, setSendingMessages] = useState(false);

  const [messageTypeFilter, setMessageTypeFilter] =
    useState("all");

  const [selectedStudent, setSelectedStudent] =
    useState(null);

  const [filters, setFilters] =
    useState({
      session_id: "",
      course_id: "",
      batch_id: BATCH_NOT_SELECTED,
      staff_id: "",
      attendance: "",
      date: getToday(),
    });

  /* =======================================================
     SESSIONS
  ======================================================= */

  useEffect(() => {
    try {
      const stored = JSON.parse(
        localStorage.getItem("sessions") ||
        "[]"
      );

      setSessions(
        Array.isArray(stored)
          ? stored
          : []
      );
    } catch {
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    if (!sessions.length) return;

    if (filters.session_id) return;

    const active = sessions.find(
      (session) =>
        Number(session?.status) === 1
    );

    if (!active?.id) return;

    setFilters((previous) => ({
      ...previous,
      session_id: String(active.id),
      batch_id: BATCH_NOT_SELECTED,
    }));
  }, [sessions, filters.session_id]);

  /* =======================================================
     MASTER DATA
  ======================================================= */

  const getCourses = async () => {
    try {
      setFilterLoading(true);

      const response =
        await courseList();

      if (response?.data?.status) {
        setCourses(
          Array.isArray(
            response?.data?.data
          )
            ? response.data.data
            : []
        );
      } else {
        setCourses([]);
      }
    } catch (error) {
      setCourses([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load courses."
      );
    } finally {
      setFilterLoading(false);
    }
  };

  const getStaffs = async () => {
    try {
      setFilterLoading(true);

      const response =
        await StaffList("status=1");

      if (response?.data?.status) {
        setStaffs(
          Array.isArray(
            response?.data?.data
          )
            ? response.data.data
            : []
        );
      } else {
        setStaffs([]);
      }
    } catch (error) {
      setStaffs([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load teachers."
      );
    } finally {
      setFilterLoading(false);
    }
  };

  const getBatches = async () => {
    if (!filters.session_id) {
      setBatches([]);
      return;
    }

    try {
      setFilterLoading(true);

      let addUrl = "";

      addUrl +=
        `&session_id=${encodeURIComponent(
          filters.session_id
        )}`;

      if (filters.course_id) {
        addUrl +=
          `&course_id=${encodeURIComponent(
            filters.course_id
          )}`;
      }

      const response =
        await batchList(addUrl);

      if (response?.data?.status) {
        setBatches(
          Array.isArray(
            response?.data?.data
          )
            ? response.data.data
            : []
        );
      } else {
        setBatches([]);
      }
    } catch (error) {
      setBatches([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load batches."
      );
    } finally {
      setFilterLoading(false);
    }
  };

  useEffect(() => {
    getCourses();
    getStaffs();
  }, []);

  useEffect(() => {
    getBatches();
    // eslint-disable-next-line
  }, [
    filters.session_id,
    filters.course_id,
  ]);

  /* =======================================================
     QUERY
  ======================================================= */

  const hasBatchSelection =
    filters.batch_id !==
    BATCH_NOT_SELECTED;

  const buildQuery = () => {
    const query = {
      session_id: filters.session_id,
      course_id: filters.course_id,
      staff_id: filters.staff_id,
      attendance: filters.attendance,
      date: filters.date,
    };

    if (
      filters.batch_id &&
      filters.batch_id !== ALL_BATCHES
    ) {
      query.batch_id =
        filters.batch_id;
    }

    let addUrl = "";

    Object.entries(query).forEach(
      ([key, value]) => {
        if (
          value !== undefined &&
          value !== null &&
          String(value) !== ""
        ) {
          addUrl +=
            `&${key}=${encodeURIComponent(
              value
            )}`;
        }
      }
    );

    return addUrl;
  };

  /* =======================================================
     ATTENDANCE / MESSAGE API
  ======================================================= */

  const getReport = async () => {
    if (!hasBatchSelection) {
      setData({});
      return;
    }

    if (
      !filters.session_id ||
      !filters.date
    ) {
      setData({});
      return;
    }

    try {
      setLoading(true);

      const response =
        await studentAttendanceList(
          buildQuery()
        );

      if (response?.data?.status) {
        setData(
          response?.data?.data || {}
        );
      } else {
        setData({});

        if (
          response?.data?.message
        ) {
          SweetAlertShow(
            "info",
            "Information",
            response.data.message
          );
        }
      }
    } catch (error) {
      console.error(
        "Message delivery report error:",
        error
      );

      setData({});

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load message delivery report."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasBatchSelection) {
      setData({});
      return;
    }

    getReport();
    // eslint-disable-next-line
  }, [
    filters.session_id,
    filters.course_id,
    filters.batch_id,
    filters.staff_id,
    filters.attendance,
    filters.date,
  ]);

  /* =======================================================
     ROWS
  ======================================================= */

  const rawRows = useMemo(() => {
    if (
      Array.isArray(data?.data)
    ) {
      return data.data;
    }

    if (Array.isArray(data)) {
      return data;
    }

    return [];
  }, [data]);

  /* =======================================================
     MESSAGE HISTORY MAPPING

     IN:
       Meta = in_message_status
       Push = push_in_message_status

     OUT:
       Meta = out_message_status
       Push = push_out_message_status

     GENERAL:
       Meta = message_status
       Push = push_message_status
  ======================================================= */


  const getStudentDelivery = (item) => {
    const attendance = item?.today_attendance || {};

    // IN message event
    const inEvent = createMessageEvent({
      key: "in",
      label: "Punch IN",
      time: attendance?.check_in_time,
      metaStatus: attendance?.in_message_status,
      pushStatus: attendance?.push_in_message_status,
    });

    // OUT message event
    const outEvent = createMessageEvent({
      key: "out",
      label: "Punch OUT",
      time: attendance?.check_out_time,
      metaStatus: attendance?.out_message_status,
      pushStatus: attendance?.push_out_message_status,
    });

    // General attendance message event (Absent / fallback)
    const generalEvent = createMessageEvent({
      key: "general",
      label: "Attendance Message",
      time: attendance?.check_in_time,
      metaStatus: attendance?.message_status,
      pushStatus: attendance?.push_message_status,
    });

    const events = [];

    const attendanceStatus = Number(attendance?.check_in);
    const checkOutStatus = Number(attendance?.check_out);

    if (attendanceStatus === 2) {
      // Absent student: general message status
      if (
        attendance?.message_status != null ||
        attendance?.push_message_status != null
      ) {
        events.push(generalEvent);
      }
    } else {
      // Present student: IN message status
      if (
        attendance?.check_in_time ||
        attendance?.in_message_status != null ||
        attendance?.push_in_message_status != null
      ) {
        events.push(inEvent);
      }

      // OUT status only after checkout
      if (
        checkOutStatus === 1 &&
        (
          attendance?.check_out_time ||
          attendance?.out_message_status != null ||
          attendance?.push_out_message_status != null
        )
      ) {
        events.push(outEvent);
      }

      // Fallback when no IN/OUT event exists
      if (
        events.length === 0 &&
        (
          attendance?.message_status != null ||
          attendance?.push_message_status != null
        )
      ) {
        events.push(generalEvent);
      }
    }

    const deliveredEvents = events.filter(
      (event) => event.hasDelivered
    );

    const hasMetaDelivered = events.some(
      (event) => event.meta.delivered
    );

    const hasPushDelivered = events.some(
      (event) => event.push.delivered
    );

    const hasProcessing = events.some(
      (event) => event.hasProcessing
    );

    const hasFailed = events.some(
      (event) => event.hasFailed
    );

    const hasDelivered = deliveredEvents.length > 0;

    const deliveredVia = [];

    if (hasMetaDelivered) {
      deliveredVia.push("WhatsApp / Meta");
    }

    if (hasPushDelivered) {
      deliveredVia.push("Push Notification");
    }

    return {
      attendance,
      inEvent,
      outEvent,
      generalEvent,
      events,
      deliveredEvents,
      hasDelivered,
      hasMetaDelivered,
      hasPushDelivered,
      hasProcessing,
      hasFailed,
      deliveredVia,
      overall: hasDelivered
        ? getMessageStatus(1)
        : hasProcessing
          ? getMessageStatus(3)
          : hasFailed
            ? getMessageStatus(4)
            : getMessageStatus(2),
    };
  };

  // const getStudentDelivery = (
  //   item
  // ) => {
  //   const attendance =
  //     item?.today_attendance || {};

  //   const inEvent =
  //     createMessageEvent({
  //       key: "in",
  //       label: "Check IN",
  //       time:
  //         attendance?.check_in_time,
  //       metaStatus:
  //         attendance?.in_message_status,
  //       pushStatus:
  //         attendance?.push_in_message_status,
  //     });

  //   const outEvent =
  //     createMessageEvent({
  //       key: "out",
  //       label: "Check OUT",
  //       time:
  //         attendance?.check_out_time,
  //       metaStatus:
  //         attendance?.out_message_status,
  //       pushStatus:
  //         attendance?.push_out_message_status,
  //     });

  //   const generalEvent =
  //     createMessageEvent({
  //       key: "general",
  //       label: "Attendance",
  //       time: null,
  //       metaStatus:
  //         attendance?.message_status,
  //       pushStatus:
  //         attendance?.push_message_status,
  //     });

  //   const events = [];

  //   if (
  //     attendance?.check_in_time ||
  //     attendance?.in_message_status ||
  //     attendance?.push_in_message_status
  //   ) {
  //     events.push(inEvent);
  //   }

  //   if (
  //     attendance?.check_out_time ||
  //     attendance?.out_message_status ||
  //     attendance?.push_out_message_status
  //   ) {
  //     events.push(outEvent);
  //   }

  //   /*
  //    * General status is useful for manual Present/Absent
  //    * attendance. Avoid duplicate history when IN/OUT
  //    * statuses are already available.
  //    */
  //   if (
  //     events.length === 0 &&
  //     (
  //       attendance?.message_status ||
  //       attendance?.push_message_status
  //     )
  //   ) {
  //     events.push(generalEvent);
  //   }

  //   const deliveredEvents =
  //     events.filter(
  //       (event) =>
  //         event.hasDelivered
  //     );

  //   const hasMetaDelivered =
  //     events.some(
  //       (event) =>
  //         event.meta.delivered
  //     );

  //   const hasPushDelivered =
  //     events.some(
  //       (event) =>
  //         event.push.delivered
  //     );

  //   const hasProcessing =
  //     events.some(
  //       (event) =>
  //         event.hasProcessing
  //     );

  //   const hasFailed =
  //     events.some(
  //       (event) =>
  //         event.hasFailed
  //     );

  //   const hasDelivered =
  //     deliveredEvents.length > 0;

  //   const deliveredVia = [];

  //   if (hasMetaDelivered) {
  //     deliveredVia.push(
  //       "WhatsApp / Meta"
  //     );
  //   }

  //   if (hasPushDelivered) {
  //     deliveredVia.push(
  //       "Push Notification"
  //     );
  //   }

  //   return {
  //     attendance,
  //     inEvent,
  //     outEvent,
  //     generalEvent,
  //     events,
  //     deliveredEvents,
  //     hasDelivered,
  //     hasMetaDelivered,
  //     hasPushDelivered,
  //     hasProcessing,
  //     hasFailed,
  //     deliveredVia,
  //     overall: hasDelivered
  //       ? getMessageStatus(1)
  //       : hasProcessing
  //       ? getMessageStatus(3)
  //       : hasFailed
  //       ? getMessageStatus(4)
  //       : getMessageStatus(2),
  //   };
  // };

  const reportRows = useMemo(() => {
    const keyword =
      search.trim().toLowerCase();

    return rawRows.filter(
      (item) => {
        const delivery =
          getStudentDelivery(item);

        if (keyword) {
          const haystack = [
            item?.name,
            item?.registration_no,
            item?.phone,
            item?.mobile,
            item?.father_name,
            item?.latest_course_and_fee
              ?.batch?.title,
            item?.latest_course_and_fee
              ?.course?.title,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          if (
            !haystack.includes(
              keyword
            )
          ) {
            return false;
          }
        }

        if (
          deliveryFilter ===
          "delivered" &&
          !delivery.hasDelivered
        ) {
          return false;
        }

        if (
          deliveryFilter ===
          "not_delivered" &&
          delivery.hasDelivered
        ) {
          return false;
        }

        if (
          deliveryFilter ===
          "processing" &&
          !delivery.hasProcessing
        ) {
          return false;
        }

        if (
          deliveryFilter ===
          "failed" &&
          !delivery.hasFailed
        ) {
          return false;
        }

        if (
          messageTypeFilter ===
          "meta" &&
          !delivery.hasMetaDelivered
        ) {
          return false;
        }

        if (
          messageTypeFilter ===
          "push" &&
          !delivery.hasPushDelivered
        ) {
          return false;
        }

        if (
          messageTypeFilter ===
          "both" &&
          !(
            delivery.hasMetaDelivered &&
            delivery.hasPushDelivered
          )
        ) {
          return false;
        }

        return true;
      }
    );
  }, [
    rawRows,
    search,
    deliveryFilter,
    messageTypeFilter,
  ]);

  /* =======================================================
     SUMMARY
  ======================================================= */

  const summary = useMemo(() => {
    const result = {
      total: rawRows.length,
      deliveredStudents: 0,
      notDeliveredStudents: 0,
      metaDeliveredStudents: 0,
      pushDeliveredStudents: 0,
      bothDeliveredStudents: 0,

      in: {
        metaSent: 0,
        pushSent: 0,
        metaNotSent: 0,
        pushNotSent: 0,
        processing: 0,
        failed: 0,
      },

      out: {
        metaSent: 0,
        pushSent: 0,
        metaNotSent: 0,
        pushNotSent: 0,
        processing: 0,
        failed: 0,
      },
    };

    rawRows.forEach((item) => {
      const delivery =
        getStudentDelivery(item);

      if (
        delivery.hasDelivered
      ) {
        result.deliveredStudents += 1;
      } else {
        result.notDeliveredStudents += 1;
      }

      if (
        delivery.hasMetaDelivered
      ) {
        result.metaDeliveredStudents += 1;
      }

      if (
        delivery.hasPushDelivered
      ) {
        result.pushDeliveredStudents += 1;
      }

      if (
        delivery.hasMetaDelivered &&
        delivery.hasPushDelivered
      ) {
        result.bothDeliveredStudents += 1;
      }

      const countEvent = (
        event,
        target
      ) => {
        if (event.meta.delivered) {
          target.metaSent += 1;
        } else {
          target.metaNotSent += 1;
        }

        if (event.push.delivered) {
          target.pushSent += 1;
        } else {
          target.pushNotSent += 1;
        }

        if (event.hasProcessing) {
          target.processing += 1;
        }

        if (event.hasFailed) {
          target.failed += 1;
        }
      };

      if (
        delivery.events.some(
          (event) =>
            event.key === "in"
        )
      ) {
        countEvent(
          delivery.inEvent,
          result.in
        );
      }

      if (
        delivery.events.some(
          (event) =>
            event.key === "out"
        )
      ) {
        countEvent(
          delivery.outEvent,
          result.out
        );
      }
    });

    return result;
  }, [rawRows]);

  /* =======================================================
     SELECTED LABELS
  ======================================================= */

  const selectedSession =
    sessions.find(
      (item) =>
        String(item?.id) ===
        String(filters.session_id)
    );

  const selectedCourse =
    courses.find(
      (item) =>
        String(item?.id) ===
        String(filters.course_id)
    );

  const selectedBatch =
    filters.batch_id === ALL_BATCHES
      ? null
      : batches.find(
        (item) =>
          String(item?.id) ===
          String(filters.batch_id)
      );

  const selectedStaff =
    staffs.find(
      (item) =>
        String(item?.id) ===
        String(filters.staff_id)
    );

  const sessionLabel =
    selectedSession?.session ||
    selectedSession?.title ||
    "-";

  const courseLabel =
    selectedCourse?.title ||
    "All Courses";

  const batchLabel =
    filters.batch_id ===
      ALL_BATCHES
      ? "All Batches"
      : selectedBatch?.title ||
      "-";


  const sendAttendanceMessages = async () => {
    if (sendingMessages) return;

    if (!hasBatchSelection) {
      SweetAlertShow(
        "info",
        "Select Batch",
        "Please select a batch before sending messages."
      );
      return;
    }

    if (
      deliveryFilter !== "not_delivered" &&
      deliveryFilter !== "failed"
    ) {
      SweetAlertShow(
        "info",
        "Select Delivery Status",
        "Please select Not Delivered or Failed first."
      );
      return;
    }

    const isFailedMode = deliveryFilter === "failed";

    const messageTypeLabel =
      messageType === "present"
        ? "Present Only"
        : messageType === "absent"
          ? "Absent Only"
          : "Both Present and Absent";

    const actionLabel = isFailedMode
      ? `retry failed attendance messages for ${messageTypeLabel} students`
      : `send attendance messages to ${messageTypeLabel} students`;

    const confirmed = window.confirm(
      `Are you sure you want to ${actionLabel}? Batch: ${batchLabel}`
    );

    if (!confirmed) return;

    setSendingMessages(true);

    try {
      const addUrl =
        filters.batch_id === ALL_BATCHES
          ? "batch_id=all"
          : `batch_id=${encodeURIComponent(filters.batch_id)}`;

      let response;

      if (isFailedMode) {
        // Failed + Present -> Present-specific failed-message API
        if (messageType === "present") {
          response = await studentAttendancePresentFailedSendMessage(addUrl);
        }
        // Failed + Absent -> Absent-specific failed-message API
        else if (messageType === "absent") {
          response = await studentAttendanceAbsentFailedSendMessage(addUrl);
        }
        // Failed + Both -> API that retries both Present and Absent failed messages
        else {
          response = await studentAttendanceFailedSendMessage(addUrl);
        }
      } else {
        // Not Delivered + Present -> Present-specific API
        if (messageType === "present") {
          response = await studentAttendancePresentSendMessage(addUrl);
        }
        // Not Delivered + Absent -> Absent-specific API
        else if (messageType === "absent") {
          response = await studentAttendanceAbsentSendMessage(addUrl);
        }
        // Not Delivered + Both -> API that sends both Present and Absent messages
        else {
          response = await studentAttendanceSendMessage(addUrl);
        }
      }

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Success",
          response.data.message ||
          (isFailedMode
            ? `Failed attendance messages for ${messageTypeLabel} students have been scheduled for retry successfully.`
            : `Attendance messages for ${messageTypeLabel} students have been scheduled successfully.`)
        );
      } else {
        SweetAlertShow(
          "error",
          "Error",
          response?.data?.message ||
          (isFailedMode
            ? "An error occurred while retrying failed attendance messages."
            : "An error occurred while sending attendance messages.")
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        (deliveryFilter === "failed"
          ? "An error occurred while retrying failed attendance messages."
          : "An error occurred while sending attendance messages.")
      );
    } finally {
      setSendingMessages(false);
    }
  };

  const staffLabel =
    selectedStaff?.name ||
    "All Teachers";

  /* =======================================================
     FILTER HANDLERS
  ======================================================= */

  const handleSessionChange = (
    event
  ) => {
    setData({});
    setSearch("");
    setDeliveryFilter("all");
    setMessageTypeFilter("all");

    setFilters((previous) => ({
      ...previous,
      session_id:
        event.target.value,
      course_id: "",
      batch_id:
        BATCH_NOT_SELECTED,
    }));
  };

  const handleCourseChange = (
    event
  ) => {
    setData({});
    setSearch("");
    setDeliveryFilter("all");
    setMessageTypeFilter("all");

    setFilters((previous) => ({
      ...previous,
      course_id:
        event.target.value,
      batch_id:
        BATCH_NOT_SELECTED,
    }));
  };

  const handleBatchChange = (
    event
  ) => {
    setData({});
    setSearch("");
    setDeliveryFilter("all");
    setMessageTypeFilter("all");

    setFilters((previous) => ({
      ...previous,
      batch_id:
        event.target.value,
    }));
  };

  const resetFilters = () => {
    const active =
      sessions.find(
        (session) =>
          Number(session?.status) === 1
      );

    setData({});
    setSearch("");
    setDeliveryFilter("all");
    setMessageTypeFilter("all");

    setFilters({
      session_id:
        active?.id
          ? String(active.id)
          : "",
      course_id: "",
      batch_id:
        BATCH_NOT_SELECTED,
      staff_id: "",
      attendance: "",
      date: getToday(),
    });
  };

  /* =======================================================
     PRINT
  ======================================================= */

  const buildPrintableRows = (
    rows
  ) =>
    rows
      .map((item, index) => {
        const delivery =
          getStudentDelivery(item);

        const attendance =
          delivery.attendance;

        const inMeta =
          delivery.inEvent.meta;
        const inPush =
          delivery.inEvent.push;
        const outMeta =
          delivery.outEvent.meta;
        const outPush =
          delivery.outEvent.push;

        const hasIn =
          delivery.events.some(
            (event) =>
              event.key === "in"
          );

        const hasOut =
          delivery.events.some(
            (event) =>
              event.key === "out"
          );

        return `
          <tr>
            <td>${index + 1}</td>

            <td class="student-cell">
              <strong>
                ${escapeHtml(
          item?.name || "-"
        )}
              </strong>
              <small>
                ${escapeHtml(
          item?.registration_no ||
          "-"
        )}
              </small>
            </td>

            <td>
              ${escapeHtml(
          item?.phone ||
          item?.mobile ||
          "-"
        )}
            </td>

            <td>
              ${Number(
          attendance?.check_in
        ) === 1
            ? "Present"
            : Number(
              attendance?.check_in
            ) === 2
              ? "Absent"
              : "Not Marked"
          }
            </td>

            <td>
              ${hasIn
            ? safeTime(
              attendance?.check_in_time
            )
            : "-"
          }
            </td>

            <td class="${inMeta.delivered
            ? "ok"
            : ""
          }">
              ${hasIn
            ? inMeta.label
            : "-"
          }
            </td>

            <td class="${inPush.delivered
            ? "ok"
            : ""
          }">
              ${hasIn
            ? inPush.label
            : "-"
          }
            </td>

            <td>
              ${hasOut
            ? safeTime(
              attendance?.check_out_time
            )
            : "-"
          }
            </td>

            <td class="${outMeta.delivered
            ? "ok"
            : ""
          }">
              ${hasOut
            ? outMeta.label
            : "-"
          }
            </td>

            <td class="${outPush.delivered
            ? "ok"
            : ""
          }">
              ${hasOut
            ? outPush.label
            : "-"
          }
            </td>

            <td>
              ${delivery.hasDelivered
            ? `Delivered via ${escapeHtml(
              delivery.deliveredVia.join(
                " + "
              )
            )}`
            : delivery.overall.label
          }
            </td>
          </tr>
        `;
      })
      .join("");

  const printReport = () => {
    if (!hasBatchSelection) {
      SweetAlertShow(
        "info",
        "Select Batch",
        "Please select a batch first."
      );
      return;
    }

    if (!reportRows.length) {
      SweetAlertShow(
        "info",
        "No Data",
        "There are no students to print."
      );
      return;
    }

    const printWindow =
      window.open(
        "",
        "_blank",
        "width=1400,height=900"
      );

    if (!printWindow) {
      SweetAlertShow(
        "info",
        "Popup Blocked",
        "Please allow popups to print this report."
      );
      return;
    }

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>
            Attendance Message Delivery Report
          </title>

          <style>
            * {
              box-sizing: border-box;
            }

            body {
              margin: 0;
              padding: 20px;
              color: #222;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 10px;
            }

            .report-header {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 20px;
              padding-bottom: 12px;
              margin-bottom: 12px;
              border-bottom: 2px solid #222;
            }

            h1 {
              margin: 0 0 5px;
              font-size: 20px;
            }

            .sub {
              color: #555;
              font-size: 10px;
            }

            .date-box {
              text-align: right;
              font-size: 10px;
            }

            .meta {
              display: grid;
              grid-template-columns: repeat(5, 1fr);
              gap: 6px;
              margin-bottom: 12px;
            }

            .meta div {
              padding: 7px;
              border: 1px solid #ddd;
              border-radius: 4px;
            }

            .meta small {
              display: block;
              margin-bottom: 2px;
              color: #777;
              font-size: 8px;
            }

            .meta strong {
              font-size: 10px;
            }

            .summary {
              display: grid;
              grid-template-columns: repeat(5, 1fr);
              gap: 6px;
              margin-bottom: 12px;
            }

            .summary div {
              padding: 8px;
              border: 1px solid #ddd;
              text-align: center;
            }

            .summary small {
              display: block;
              color: #666;
              font-size: 8px;
            }

            .summary strong {
              display: block;
              margin-top: 2px;
              font-size: 15px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
            }

            th,
            td {
              padding: 5px 4px;
              border: 1px solid #cfcfcf;
              vertical-align: middle;
              text-align: center;
              word-break: break-word;
            }

            th {
              background: #f1f1f1;
              font-size: 8px;
            }

            .student-cell {
              text-align: left;
            }

            .student-cell strong,
            .student-cell small {
              display: block;
            }

            .student-cell small {
              margin-top: 2px;
              color: #666;
              font-size: 8px;
            }

            .ok {
              font-weight: bold;
            }

            .footer {
              display: flex;
              justify-content: space-between;
              margin-top: 25px;
              padding-top: 10px;
              font-size: 9px;
            }

            @page {
              size: A4 landscape;
              margin: 8mm;
            }

            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>

        <body>
          <div class="report-header">
            <div>
              <h1>
                Attendance Message Delivery Report
              </h1>

              <div class="sub">
                Date-wise WhatsApp / Meta and Push Notification delivery report
              </div>
            </div>

            <div class="date-box">
              <strong>
                ${escapeHtml(
      formatDateReadable(
        filters.date
      )
    )}
              </strong>
              <br />
              Printed:
              ${escapeHtml(
      new Date().toLocaleString(
        "en-IN"
      )
    )}
            </div>
          </div>

          <div class="meta">
            <div>
              <small>Session</small>
              <strong>
                ${escapeHtml(
      sessionLabel
    )}
              </strong>
            </div>

            <div>
              <small>Course</small>
              <strong>
                ${escapeHtml(
      courseLabel
    )}
              </strong>
            </div>

            <div>
              <small>Batch</small>
              <strong>
                ${escapeHtml(
      batchLabel
    )}
              </strong>
            </div>

            <div>
              <small>Class Teacher</small>
              <strong>
                ${escapeHtml(
      staffLabel
    )}
              </strong>
            </div>

            <div>
              <small>Students Printed</small>
              <strong>
                ${reportRows.length}
              </strong>
            </div>
          </div>

          <div class="summary">
            <div>
              <small>Total Students</small>
              <strong>
                ${summary.total}
              </strong>
            </div>

            <div>
              <small>Any Message Delivered</small>
              <strong>
                ${summary.deliveredStudents}
              </strong>
            </div>

            <div>
              <small>WhatsApp / Meta Delivered</small>
              <strong>
                ${summary.metaDeliveredStudents}
              </strong>
            </div>

            <div>
              <small>Push Delivered</small>
              <strong>
                ${summary.pushDeliveredStudents}
              </strong>
            </div>

            <div>
              <small>No Delivery</small>
              <strong>
                ${summary.notDeliveredStudents}
              </strong>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width:3%">#</th>
                <th style="width:12%">Student</th>
                <th style="width:8%">Mobile</th>
                <th style="width:7%">Attendance</th>
                <th style="width:7%">IN Time</th>
                <th style="width:9%">IN Meta</th>
                <th style="width:9%">IN Push</th>
                <th style="width:7%">OUT Time</th>
                <th style="width:9%">OUT Meta</th>
                <th style="width:9%">OUT Push</th>
                <th style="width:14%">Delivery Result</th>
              </tr>
            </thead>

            <tbody>
              ${buildPrintableRows(
      reportRows
    )}
            </tbody>
          </table>

          <div class="footer">
            <span>
              Generated from Attendance Message Delivery
            </span>

            <span>
              Authorized Signature: __________________
            </span>
          </div>

          <script>
            window.onload = function () {
              window.focus();
              window.print();
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  /* =======================================================
     EXCEL EXPORT
  ======================================================= */

  const exportExcel = () => {
    if (!hasBatchSelection) {
      SweetAlertShow(
        "info",
        "Select Batch",
        "Please select a batch first."
      );
      return;
    }

    if (!reportRows.length) {
      SweetAlertShow(
        "info",
        "No Data",
        "There are no students to export."
      );
      return;
    }

    const excelRows =
      reportRows
        .map((item, index) => {
          const delivery =
            getStudentDelivery(item);

          const attendance =
            delivery.attendance;

          const hasIn =
            delivery.events.some(
              (event) =>
                event.key === "in"
            );

          const hasOut =
            delivery.events.some(
              (event) =>
                event.key === "out"
            );

          return `
            <tr>
              <td>${index + 1}</td>
              <td>${escapeHtml(
            item?.name || "-"
          )}</td>
              <td>${escapeHtml(
            item?.registration_no ||
            "-"
          )}</td>
              <td>${escapeHtml(
            item?.phone ||
            item?.mobile ||
            "-"
          )}</td>
              <td>${escapeHtml(
            item
              ?.latest_course_and_fee
              ?.course?.title ||
            courseLabel
          )}</td>
              <td>${escapeHtml(
            item
              ?.latest_course_and_fee
              ?.batch?.title ||
            batchLabel
          )}</td>
              <td>
                ${Number(
            attendance?.check_in
          ) === 1
              ? "Present"
              : Number(
                attendance?.check_in
              ) === 2
                ? "Absent"
                : "Not Marked"
            }
              </td>
              <td>${hasIn
              ? safeTime(
                attendance?.check_in_time
              )
              : "-"
            }</td>
              <td>${hasIn
              ? delivery.inEvent.meta.label
              : "-"
            }</td>
              <td>${hasIn
              ? delivery.inEvent.push.label
              : "-"
            }</td>
              <td>${hasOut
              ? safeTime(
                attendance?.check_out_time
              )
              : "-"
            }</td>
              <td>${hasOut
              ? delivery.outEvent.meta.label
              : "-"
            }</td>
              <td>${hasOut
              ? delivery.outEvent.push.label
              : "-"
            }</td>
              <td>${delivery.hasDelivered
              ? `Delivered via ${escapeHtml(
                delivery.deliveredVia.join(
                  " + "
                )
              )}`
              : delivery.overall.label
            }</td>
            </tr>
          `;
        })
        .join("");

    const workbook = `
      <html>
        <head>
          <meta charset="UTF-8" />
        </head>

        <body>
          <table border="1">
            <tr>
              <th colspan="14">
                Attendance Message Delivery Report
              </th>
            </tr>

            <tr>
              <td>Report Date</td>
              <td colspan="13">
                ${escapeHtml(
      formatDateReadable(
        filters.date
      )
    )}
              </td>
            </tr>

            <tr>
              <td>Session</td>
              <td colspan="3">
                ${escapeHtml(
      sessionLabel
    )}
              </td>

              <td>Course</td>
              <td colspan="3">
                ${escapeHtml(
      courseLabel
    )}
              </td>

              <td>Batch</td>
              <td colspan="3">
                ${escapeHtml(
      batchLabel
    )}
              </td>

              <td>Teacher</td>
              <td>
                ${escapeHtml(
      staffLabel
    )}
              </td>
            </tr>

            <tr></tr>

            <tr>
              <th>#</th>
              <th>Student</th>
              <th>Registration No</th>
              <th>Mobile</th>
              <th>Course</th>
              <th>Batch</th>
              <th>Attendance</th>
              <th>IN Time</th>
              <th>IN WhatsApp / Meta</th>
              <th>IN Push</th>
              <th>OUT Time</th>
              <th>OUT WhatsApp / Meta</th>
              <th>OUT Push</th>
              <th>Overall Delivery</th>
            </tr>

            ${excelRows}
          </table>
        </body>
      </html>
    `;

    const blob = new Blob(
      [workbook],
      {
        type:
          "application/vnd.ms-excel;charset=utf-8;",
      }
    );

    const url =
      URL.createObjectURL(blob);

    const anchor =
      document.createElement("a");

    anchor.href = url;

    anchor.download =
      `attendance-message-delivery-${filters.date}-${String(
        batchLabel
      )
        .replace(
          /[^a-z0-9]+/gi,
          "-"
        )
        .toLowerCase()}.xls`;

    document.body.appendChild(
      anchor
    );

    anchor.click();
    anchor.remove();

    URL.revokeObjectURL(url);
  };

  /* =======================================================
     UI HELPERS
  ======================================================= */

  const DeliveryBadge = ({
    channel,
  }) => (
    <span
      className={`delivery-badge ${channel.className}`}
    >
      <i
        className={
          channel.icon
        }
      />
      {channel.label}
    </span>
  );

  const EventChannel = ({
    title,
    event,
    icon,
  }) => (
    <div className="event-channel-card">
      <div className="event-channel-title">
        <span>
          <i className={icon} />
        </span>

        <div>
          <strong>
            {title}
          </strong>

          <small>
            {event.time
              ? safeTime(event.time)
              : "Attendance message"}
          </small>
        </div>
      </div>

      <div className="event-channel-statuses">
        <div>
          <small>
            WhatsApp / Meta
          </small>
          <DeliveryBadge
            channel={event.meta}
          />
        </div>

        <div>
          <small>
            Push Notification
          </small>
          <DeliveryBadge
            channel={event.push}
          />
        </div>
      </div>

      {event.hasDelivered && (
        <div className="event-delivered-via">
          <i className="ri-check-double-line" />
          Delivered via{" "}
          <strong>
            {event.deliveredChannels
              .map(
                (channel) =>
                  channel.label
              )
              .join(" + ")}
          </strong>
        </div>
      )}
    </div>
  );

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <>
      {(loading ||
        filterLoading) && (
          <LoadingDiv />
        )}

      <div className="layout-wrapper layout-content-navbar attendance-dashboard">
        <div className="layout-container">

          {/* SIDEBAR */}
          <div className="no-print">
            <Header />
          </div>

          <div className="layout-page message-report-layout">

            {/* TOP NAVBAR */}
            <div className="no-print">
              <Nav />
            </div>

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">

                {/* ATTENDANCE MASTER V2 */}
                <div className="no-print message-master-v2-wrapper">
                  <MasterV2 />
                </div>

                <div className="message-report-page">

                  {/* PAGE HEADER */}
                  <div className="message-page-header">
                    <div>
                      <span className="page-icon">
                        <i className="ri-message-3-line" />
                      </span>

                      <div>
                        <h4>
                          Message Delivery Report
                        </h4>

                        <p>
                          Date-wise attendance WhatsApp / Meta and Push Notification delivery status
                        </p>
                      </div>
                    </div>

                    <div className="page-header-actions">
                      <button
                        type="button"
                        className="report-action-btn secondary"
                        disabled={
                          !hasBatchSelection ||
                          !reportRows.length
                        }
                        onClick={
                          exportExcel
                        }
                      >
                        <i className="ri-file-excel-2-line" />
                        Excel
                      </button>

                      <button
                        type="button"
                        className="report-action-btn primary"
                        disabled={
                          !hasBatchSelection ||
                          !reportRows.length
                        }
                        onClick={
                          printReport
                        }
                      >
                        <i className="ri-printer-line" />
                        Print
                      </button>
                    </div>
                  </div>

                  {/* FILTERS */}
                  <div className="message-filter-card">
                    <div className="filter-card-heading">
                      <div>
                        <strong>
                          Report Filters
                        </strong>

                        <span>
                          Select a batch to load students
                        </span>
                      </div>

                      <button
                        type="button"
                        className="reset-filter-btn"
                        onClick={
                          resetFilters
                        }
                      >
                        <i className="ri-refresh-line" />
                        Reset
                      </button>
                    </div>

                    <div className="message-filter-grid">

                      <div className="report-field">
                        <label>
                          Session
                        </label>

                        <select
                          value={
                            filters.session_id
                          }
                          onChange={
                            handleSessionChange
                          }
                        >
                          <option value="">
                            Select Session
                          </option>

                          {sessions.map(
                            (session) => (
                              <option
                                key={
                                  session.id
                                }
                                value={
                                  session.id
                                }
                              >
                                {session.session ||
                                  session.title}

                                {Number(
                                  session.status
                                ) === 1
                                  ? " - Active"
                                  : ""}
                              </option>
                            )
                          )}
                        </select>
                      </div>

                      <div className="report-field">
                        <label>
                          Course
                        </label>

                        <select
                          value={
                            filters.course_id
                          }
                          onChange={
                            handleCourseChange
                          }
                        >
                          <option value="">
                            All Courses
                          </option>

                          {courses.map(
                            (course) => (
                              <option
                                key={
                                  course.id
                                }
                                value={
                                  course.id
                                }
                              >
                                {
                                  course.title
                                }
                              </option>
                            )
                          )}
                        </select>
                      </div>

                      <div className="report-field required">
                        <label>
                          Batch
                        </label>

                        <select
                          value={
                            filters.batch_id
                          }
                          onChange={
                            handleBatchChange
                          }
                        >
                          <option
                            value=""
                            disabled
                          >
                            Select Batch
                          </option>

                          <option
                            value={
                              ALL_BATCHES
                            }
                          >
                            All Batches
                          </option>

                          {batches.map(
                            (batch) => (
                              <option
                                key={
                                  batch.id
                                }
                                value={
                                  batch.id
                                }
                              >
                                {batch.title}

                                {batch?.course
                                  ?.title
                                  ? ` - ${batch.course.title}`
                                  : ""}
                              </option>
                            )
                          )}
                        </select>
                      </div>

                      <div className="report-field">
                        <label>
                          Class Teacher
                        </label>

                        <select
                          value={
                            filters.staff_id
                          }
                          onChange={(
                            event
                          ) =>
                            setFilters(
                              (
                                previous
                              ) => ({
                                ...previous,
                                staff_id:
                                  event
                                    .target
                                    .value,
                              })
                            )
                          }
                        >
                          <option value="">
                            All Teachers
                          </option>

                          {staffs.map(
                            (staff) => (
                              <option
                                key={
                                  staff.id
                                }
                                value={
                                  staff.id
                                }
                              >
                                {staff.name}
                                {staff.registration_no
                                  ? ` (${staff.registration_no})`
                                  : ""}
                              </option>
                            )
                          )}
                        </select>
                      </div>

                      <div className="report-field">
                        <label>
                          Attendance
                        </label>

                        <select
                          value={
                            filters.attendance
                          }
                          onChange={(
                            event
                          ) =>
                            setFilters(
                              (
                                previous
                              ) => ({
                                ...previous,
                                attendance:
                                  event
                                    .target
                                    .value,
                              })
                            )
                          }
                        >
                          <option value="">
                            All Attendance
                          </option>
                          <option value="1">
                            Present
                          </option>
                          <option value="2">
                            Absent
                          </option>
                          <option value="0">
                            Not Marked
                          </option>
                        </select>
                      </div>

                      <div className="report-field">
                        <label>
                          Report Date
                        </label>

                        <input
                          type="date"
                          value={
                            filters.date
                          }
                          max={getToday()}
                          onChange={(
                            event
                          ) =>
                            setFilters(
                              (
                                previous
                              ) => ({
                                ...previous,
                                date:
                                  event
                                    .target
                                    .value,
                              })
                            )
                          }
                        />
                      </div>

                    </div>
                  </div>

                  {!hasBatchSelection ? (
                    <div className="message-empty-card">
                      <span>
                        <i className="ri-stack-line" />
                      </span>

                      <h5>
                        Select Batch to View Message Delivery
                      </h5>

                      <p>
                        Choose a specific batch or All Batches. Students are not loaded until you select a batch.
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* SUMMARY */}
                      <div className="delivery-summary-grid">

                        <button
                          type="button"
                          className={`delivery-summary-card ${deliveryFilter ===
                            "all"
                            ? "active"
                            : ""
                            }`}
                          onClick={() =>
                            setDeliveryFilter(
                              "all"
                            )
                          }
                        >
                          <span className="summary-icon total">
                            <i className="ri-group-line" />
                          </span>

                          <div>
                            <small>
                              Students
                            </small>
                            <strong>
                              {summary.total}
                            </strong>
                          </div>
                        </button>

                        <button
                          type="button"
                          className={`delivery-summary-card ${deliveryFilter ===
                            "delivered"
                            ? "active"
                            : ""
                            }`}
                          onClick={() =>
                            setDeliveryFilter(
                              "delivered"
                            )
                          }
                        >
                          <span className="summary-icon delivered">
                            <i className="ri-checkbox-circle-line" />
                          </span>

                          <div>
                            <small>
                              Delivered
                            </small>
                            <strong>
                              {
                                summary.deliveredStudents
                              }
                            </strong>
                          </div>
                        </button>

                        <button
                          type="button"
                          className={`delivery-summary-card ${messageTypeFilter ===
                            "meta"
                            ? "active"
                            : ""
                            }`}
                          onClick={() =>
                            setMessageTypeFilter(
                              messageTypeFilter ===
                                "meta"
                                ? "all"
                                : "meta"
                            )
                          }
                        >
                          <span className="summary-icon whatsapp">
                            <i className="ri-whatsapp-line" />
                          </span>

                          <div>
                            <small>
                              WhatsApp / Meta
                            </small>
                            <strong>
                              {
                                summary.metaDeliveredStudents
                              }
                            </strong>
                          </div>
                        </button>

                        <button
                          type="button"
                          className={`delivery-summary-card ${messageTypeFilter ===
                            "push"
                            ? "active"
                            : ""
                            }`}
                          onClick={() =>
                            setMessageTypeFilter(
                              messageTypeFilter ===
                                "push"
                                ? "all"
                                : "push"
                            )
                          }
                        >
                          <span className="summary-icon push">
                            <i className="ri-notification-3-line" />
                          </span>

                          <div>
                            <small>
                              Push Delivered
                            </small>
                            <strong>
                              {
                                summary.pushDeliveredStudents
                              }
                            </strong>
                          </div>
                        </button>

                        <button
                          type="button"
                          className={`delivery-summary-card ${deliveryFilter ===
                            "not_delivered"
                            ? "active"
                            : ""
                            }`}
                          onClick={() =>
                            setDeliveryFilter(
                              "not_delivered"
                            )
                          }
                        >
                          <span className="summary-icon not-delivered">
                            <i className="ri-close-circle-line" />
                          </span>

                          <div>
                            <small>
                              No Delivery
                            </small>
                            <strong>
                              {
                                summary.notDeliveredStudents
                              }
                            </strong>
                          </div>
                        </button>

                      </div>

                      {/* TOOLBAR */}
                      <div className="message-report-toolbar">
                        <div className="message-search">
                          <i className="ri-search-line" />

                          <input
                            type="text"
                            value={search}
                            onChange={(
                              event
                            ) =>
                              setSearch(
                                event.target
                                  .value
                              )
                            }
                            placeholder="Search student, registration no. or mobile..."
                          />

                          {search && (
                            <button
                              type="button"
                              onClick={() =>
                                setSearch("")
                              }
                            >
                              <i className="ri-close-line" />
                            </button>
                          )}
                        </div>

                        <div className="toolbar-filters">
                          <select
                            value={
                              deliveryFilter
                            }
                            onChange={(
                              event
                            ) =>
                              setDeliveryFilter(
                                event.target
                                  .value
                              )
                            }
                          >
                            <option value="all">
                              All Delivery
                            </option>
                            <option value="delivered">
                              Delivered
                            </option>
                            <option value="not_delivered">
                              Not Delivered
                            </option>
                            <option value="processing">
                              Processing
                            </option>
                            <option value="failed">
                              Failed
                            </option>
                          </select>

                          {(deliveryFilter === "not_delivered" || deliveryFilter === "failed") && (
                            <>
                              <select
                                className="attendance-resend-type"
                                value={messageType}
                                onChange={(event) =>
                                  setMessageType(event.target.value)
                                }
                                disabled={sendingMessages}
                                aria-label={deliveryFilter === "failed" ? "Failed attendance message type" : "Attendance message type"}
                              >
                                <option value="present">Present</option>
                                <option value="absent">Absent</option>
                                <option value="both">Both</option>
                              </select>

                              <button
                                type="button"
                                className="attendance-resend-btn"
                                onClick={sendAttendanceMessages}
                                disabled={sendingMessages || !hasBatchSelection}
                                title={deliveryFilter === "failed" ? "Retry failed attendance messages" : "Send attendance messages"}
                              >
                                {sendingMessages ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm" />
                                    <span>Sending</span>
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-send-plane-fill" />
                                    <span>Send</span>
                                  </>
                                )}
                              </button>
                            </>
                          )}

                          <select
                            value={
                              messageTypeFilter
                            }
                            onChange={(
                              event
                            ) =>
                              setMessageTypeFilter(
                                event.target
                                  .value
                              )
                            }
                          >
                            <option value="all">
                              All Channels
                            </option>
                            <option value="meta">
                              WhatsApp / Meta Delivered
                            </option>
                            <option value="push">
                              Push Delivered
                            </option>
                            <option value="both">
                              Both Delivered
                            </option>
                          </select>

                          <button
                            type="button"
                            className="refresh-report-btn"
                            onClick={
                              getReport
                            }
                          >
                            <i className="ri-refresh-line" />
                            Refresh
                          </button>
                        </div>
                      </div>

                      {/* REPORT TABLE */}
                      <div className="message-report-card">
                        <div className="report-card-head">
                          <div>
                            <strong>
                              Student Delivery
                            </strong>

                            <span>
                              {reportRows.length} of{" "}
                              {rawRows.length} students
                            </span>
                          </div>

                          <span>
                            {formatDateReadable(
                              filters.date
                            )}
                          </span>
                        </div>

                        {reportRows.length ? (
                          <>
                            <div className="message-table-wrap">
                              <table className="message-delivery-table">
                                <thead>
                                  <tr>
                                    <th>
                                      Student
                                    </th>

                                    <th>
                                      Attendance
                                    </th>

                                    <th>
                                      Check IN
                                    </th>

                                    <th>
                                      IN WhatsApp / Meta
                                    </th>

                                    <th>
                                      IN Push
                                    </th>

                                    <th>
                                      Check OUT
                                    </th>

                                    <th>
                                      OUT WhatsApp / Meta
                                    </th>

                                    <th>
                                      OUT Push
                                    </th>

                                    <th>
                                      Delivery
                                    </th>

                                    <th>
                                      Details
                                    </th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {reportRows.map(
                                    (
                                      item,
                                      index
                                    ) => {
                                      const delivery =
                                        getStudentDelivery(
                                          item
                                        );

                                      const attendance =
                                        delivery.attendance;

                                      const hasIn =
                                        delivery.events.some(
                                          (
                                            event
                                          ) =>
                                            event.key ===
                                            "in"
                                        );

                                      const hasOut =
                                        delivery.events.some(
                                          (
                                            event
                                          ) =>
                                            event.key ===
                                            "out"
                                        );

                                      const attendanceLabel =
                                        Number(
                                          attendance?.check_in
                                        ) === 1
                                          ? "Present"
                                          : Number(
                                            attendance?.check_in
                                          ) === 2
                                            ? "Absent"
                                            : "Not Marked";

                                      return (
                                        <tr
                                          key={
                                            item?.id ||
                                            item?.registration_no ||
                                            index
                                          }
                                        >
                                          <td>
                                            <div className="student-info-cell">
                                              <span className="student-avatar">
                                                {getInitials(
                                                  item?.name
                                                )}
                                              </span>

                                              <div>
                                                <strong>
                                                  {item?.name ||
                                                    "-"}
                                                </strong>

                                                <small>
                                                  {item?.registration_no ||
                                                    "-"}

                                                  {(item?.phone ||
                                                    item?.mobile) &&
                                                    ` • ${item?.phone ||
                                                    item?.mobile
                                                    }`}
                                                </small>
                                              </div>
                                            </div>
                                          </td>

                                          <td>
                                            <span
                                              className={`attendance-mini ${attendanceLabel ===
                                                "Present"
                                                ? "present"
                                                : attendanceLabel ===
                                                  "Absent"
                                                  ? "absent"
                                                  : "not-marked"
                                                }`}
                                            >
                                              {
                                                attendanceLabel
                                              }
                                            </span>
                                          </td>

                                          <td>
                                            <span className="time-cell">
                                              {hasIn
                                                ? safeTime(
                                                  attendance?.check_in_time
                                                )
                                                : "-"}
                                            </span>
                                          </td>

                                          <td>
                                            {hasIn ? (
                                              <DeliveryBadge
                                                channel={
                                                  delivery
                                                    .inEvent
                                                    .meta
                                                }
                                              />
                                            ) : (
                                              <span className="table-dash">
                                                —
                                              </span>
                                            )}
                                          </td>

                                          <td>
                                            {hasIn ? (
                                              <DeliveryBadge
                                                channel={
                                                  delivery
                                                    .inEvent
                                                    .push
                                                }
                                              />
                                            ) : (
                                              <span className="table-dash">
                                                —
                                              </span>
                                            )}
                                          </td>

                                          <td>
                                            <span className="time-cell">
                                              {hasOut
                                                ? safeTime(
                                                  attendance?.check_out_time
                                                )
                                                : "-"}
                                            </span>
                                          </td>

                                          <td>
                                            {hasOut ? (
                                              <DeliveryBadge
                                                channel={
                                                  delivery
                                                    .outEvent
                                                    .meta
                                                }
                                              />
                                            ) : (
                                              <span className="table-dash">
                                                —
                                              </span>
                                            )}
                                          </td>

                                          <td>
                                            {hasOut ? (
                                              <DeliveryBadge
                                                channel={
                                                  delivery
                                                    .outEvent
                                                    .push
                                                }
                                              />
                                            ) : (
                                              <span className="table-dash">
                                                —
                                              </span>
                                            )}
                                          </td>

                                          <td>
                                            <div className="overall-delivery-cell">
                                              <span
                                                className={`delivery-badge ${delivery.overall.className}`}
                                              >
                                                <i
                                                  className={
                                                    delivery
                                                      .overall
                                                      .icon
                                                  }
                                                />
                                                {
                                                  delivery
                                                    .overall
                                                    .label
                                                }
                                              </span>

                                              {delivery.hasDelivered && (
                                                <small>
                                                  via{" "}
                                                  {delivery.deliveredVia.join(
                                                    " + "
                                                  )}
                                                </small>
                                              )}
                                            </div>
                                          </td>

                                          <td>
                                            <button
                                              type="button"
                                              className="view-history-btn"
                                              onClick={() =>
                                                setSelectedStudent(
                                                  item
                                                )
                                              }
                                            >
                                              <i className="ri-eye-line" />
                                              View
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    }
                                  )}
                                </tbody>
                              </table>
                            </div>

                            {/* MOBILE LIST */}
                            <div className="message-mobile-list">
                              {reportRows.map(
                                (
                                  item,
                                  index
                                ) => {
                                  const delivery =
                                    getStudentDelivery(
                                      item
                                    );

                                  const attendance =
                                    delivery.attendance;

                                  return (
                                    <button
                                      type="button"
                                      className="message-mobile-card"
                                      key={
                                        `mobile-${item?.id ||
                                        item?.registration_no ||
                                        index
                                        }`
                                      }
                                      onClick={() =>
                                        setSelectedStudent(
                                          item
                                        )
                                      }
                                    >
                                      <div className="mobile-card-top">
                                        <span className="student-avatar">
                                          {getInitials(
                                            item?.name
                                          )}
                                        </span>

                                        <div>
                                          <strong>
                                            {item?.name ||
                                              "-"}
                                          </strong>

                                          <small>
                                            {item?.registration_no ||
                                              "-"}
                                          </small>
                                        </div>

                                        <span
                                          className={`delivery-badge ${delivery.overall.className}`}
                                        >
                                          {
                                            delivery
                                              .overall
                                              .label
                                          }
                                        </span>
                                      </div>

                                      <div className="mobile-card-info">
                                        <span>
                                          IN
                                          <strong>
                                            {safeTime(
                                              attendance?.check_in_time
                                            )}
                                          </strong>
                                        </span>

                                        <span>
                                          OUT
                                          <strong>
                                            {safeTime(
                                              attendance?.check_out_time
                                            )}
                                          </strong>
                                        </span>

                                        <span>
                                          Delivered Via
                                          <strong>
                                            {delivery.hasDelivered
                                              ? delivery.deliveredVia.join(
                                                " + "
                                              )
                                              : "-"}
                                          </strong>
                                        </span>
                                      </div>
                                    </button>
                                  );
                                }
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="report-no-results">
                            <i className="ri-search-eye-line" />

                            <strong>
                              No Students Found
                            </strong>

                            <span>
                              No students match the selected report filters.
                            </span>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                </div>
              </div>
            </div>
          </div>
        </div>

        {/* =================================================
          STUDENT MESSAGE HISTORY MODAL
      ================================================= */}
        {selectedStudent &&
          (() => {
            const delivery =
              getStudentDelivery(
                selectedStudent
              );

            return (
              <div className="delivery-modal-backdrop">
                <div className="delivery-history-modal">

                  <div className="delivery-modal-header">
                    <div>
                      <h4>
                        <i className="ri-message-2-line" />
                        Message History
                      </h4>

                      <p>
                        Complete attendance message delivery details
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        setSelectedStudent(
                          null
                        )
                      }
                    >
                      <i className="ri-close-line" />
                    </button>
                  </div>

                  <div className="delivery-modal-body">

                    <div className="modal-student-card">
                      <span className="student-avatar large">
                        {getInitials(
                          selectedStudent?.name
                        )}
                      </span>

                      <div>
                        <strong>
                          {selectedStudent?.name ||
                            "-"}
                        </strong>

                        <small>
                          {selectedStudent?.registration_no ||
                            "-"}

                          {(selectedStudent?.phone ||
                            selectedStudent?.mobile) &&
                            ` • ${selectedStudent?.phone ||
                            selectedStudent?.mobile
                            }`}
                        </small>
                      </div>

                      <span
                        className={`delivery-badge ${delivery.overall.className}`}
                      >
                        <i
                          className={
                            delivery.overall
                              .icon
                          }
                        />
                        {
                          delivery.overall
                            .label
                        }
                      </span>
                    </div>

                    {delivery.hasDelivered && (
                      <div className="delivered-via-box">
                        <i className="ri-check-double-line" />

                        <div>
                          <small>
                            Message Delivered Via
                          </small>

                          <strong>
                            {delivery.deliveredVia.join(
                              " + "
                            )}
                          </strong>
                        </div>
                      </div>
                    )}

                    <div className="history-title">
                      <strong>
                        Delivery History
                      </strong>

                      <span>
                        {formatDateReadable(
                          filters.date
                        )}
                      </span>
                    </div>

                    <div className="history-event-list">
                      {delivery.events.length ? (
                        delivery.events.map(
                          (event) => (
                            <EventChannel
                              key={
                                event.key
                              }
                              title={
                                event.label
                              }
                              event={
                                event
                              }
                              icon={
                                event.key ===
                                  "in"
                                  ? "ri-login-box-line"
                                  : event.key ===
                                    "out"
                                    ? "ri-logout-box-r-line"
                                    : "ri-calendar-check-line"
                              }
                            />
                          )
                        )
                      ) : (
                        <div className="history-empty">
                          <i className="ri-inbox-line" />

                          <strong>
                            No Message History
                          </strong>

                          <span>
                            No attendance message status is available for this student on this date.
                          </span>
                        </div>
                      )}
                    </div>

                  </div>

                  <div className="delivery-modal-footer">
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedStudent(
                          null
                        )
                      }
                    >
                      Close
                    </button>
                  </div>

                </div>
              </div>
            );
          })()}

        {/* =================================================
          CSS
      ================================================= */}
      </div>

      <style>{`
        .message-report-layout {
          min-width: 0;
          width: 100%;
        }

        .message-report-page {
          max-width: 1700px;
          margin: 0 auto;
        }

        .message-page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          padding: 15px 16px;
          margin-bottom: 12px;
          border: 1px solid #e5e7eb;
          border-radius: 11px;
          background: #fff;
          box-shadow: 0 4px 18px rgba(34, 48, 62, .045);
        }

        .message-page-header > div:first-child {
          display: flex;
          align-items: center;
          min-width: 0;
          gap: 10px;
        }

        .page-icon {
          width: 42px;
          height: 42px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 42px;
          border-radius: 9px;
          background: #eef0ff;
          color: #696cff;
          font-size: 21px;
        }

        .message-page-header h4 {
          margin: 0;
          color: #3f444e;
          font-size: 17px;
          font-weight: 800;
        }

        .message-page-header p {
          margin: 3px 0 0;
          color: #9297a2;
          font-size: 10px;
        }

        .page-header-actions {
          display: flex;
          align-items: center;
          gap: 7px;
        }

        .report-action-btn,
        .reset-filter-btn,
        .refresh-report-btn,
        .view-history-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          border: 0;
          cursor: pointer;
          font-weight: 700;
          transition: .18s ease;
        }

        .report-action-btn {
          min-height: 36px;
          padding: 0 12px;
          border-radius: 7px;
          font-size: 10px;
        }

        .report-action-btn.primary {
          background: #696cff;
          color: #fff;
        }

        .report-action-btn.secondary {
          border: 1px solid #d8dbe3;
          background: #fff;
          color: #4e5561;
        }

        .report-action-btn:disabled {
          opacity: .45;
          cursor: not-allowed;
        }

        .message-filter-card {
          margin-bottom: 12px;
          padding: 13px 14px;
          border: 1px solid #e5e7eb;
          border-radius: 11px;
          background: #fff;
        }

        .filter-card-heading {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 10px;
        }

        .filter-card-heading > div {
          display: flex;
          flex-direction: column;
        }

        .filter-card-heading strong {
          color: #454a54;
          font-size: 11px;
        }

        .filter-card-heading span {
          margin-top: 1px;
          color: #999ea8;
          font-size: 8px;
        }

        .reset-filter-btn {
          padding: 6px 8px;
          border-radius: 6px;
          background: #f4f5f7;
          color: #69707c;
          font-size: 9px;
        }

        .message-filter-grid {
          display: grid;
          grid-template-columns: repeat(6, minmax(0, 1fr));
          gap: 8px;
        }

        .report-field {
          min-width: 0;
        }

        .report-field label {
          display: block;
          margin: 0 0 4px 2px;
          color: #727985;
          font-size: 8px;
          font-weight: 700;
        }

        .report-field.required label::after {
          content: " *";
          color: #e64b4b;
        }

        .report-field select,
        .report-field input {
          width: 100%;
          min-height: 36px;
          padding: 0 9px;
          border: 1px solid #dfe2e7;
          border-radius: 7px;
          outline: none;
          background: #fff;
          color: #454b55;
          font-size: 9px;
        }

        .report-field select:focus,
        .report-field input:focus {
          border-color: #8e91ff;
          box-shadow: 0 0 0 2px rgba(105,108,255,.08);
        }

        .message-empty-card {
          min-height: 300px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          padding: 30px;
          border: 1px dashed #d9dce3;
          border-radius: 11px;
          background: #fff;
          text-align: center;
        }

        .message-empty-card > span {
          width: 58px;
          height: 58px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 10px;
          border-radius: 14px;
          background: #eef0ff;
          color: #696cff;
          font-size: 27px;
        }

        .message-empty-card h5 {
          margin: 0;
          color: #474c56;
          font-size: 14px;
        }

        .message-empty-card p {
          max-width: 480px;
          margin: 5px 0 0;
          color: #9499a3;
          font-size: 9px;
          line-height: 1.5;
        }

        .delivery-summary-grid {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 8px;
          margin-bottom: 10px;
        }

        .delivery-summary-card {
          display: flex;
          align-items: center;
          gap: 9px;
          min-width: 0;
          padding: 10px;
          border: 1px solid #e3e6eb;
          border-radius: 9px;
          background: #fff;
          text-align: left;
          cursor: pointer;
        }

        .delivery-summary-card.active {
          border-color: #9698ff;
          box-shadow: 0 0 0 2px rgba(105,108,255,.07);
        }

        .summary-icon {
          width: 34px;
          height: 34px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 34px;
          border-radius: 8px;
          font-size: 16px;
        }

        .summary-icon.total {
          background: #eef0ff;
          color: #696cff;
        }

        .summary-icon.delivered,
        .summary-icon.whatsapp {
          background: #e8f8ef;
          color: #278b53;
        }

        .summary-icon.push {
          background: #edf0ff;
          color: #696cff;
        }

        .summary-icon.not-delivered {
          background: #fff3e2;
          color: #bb761b;
        }

        .delivery-summary-card > div {
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .delivery-summary-card small {
          overflow: hidden;
          color: #8e949f;
          font-size: 8px;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .delivery-summary-card strong {
          margin-top: 1px;
          color: #424750;
          font-size: 17px;
        }

        .message-report-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 9px 10px;
          margin-bottom: 10px;
          border: 1px solid #e4e7eb;
          border-radius: 9px;
          background: #fff;
        }

        .message-search {
          position: relative;
          flex: 1;
          max-width: 430px;
        }

        .message-search > i {
          position: absolute;
          top: 50%;
          left: 9px;
          transform: translateY(-50%);
          color: #979ca6;
          font-size: 13px;
        }

        .message-search input {
          width: 100%;
          min-height: 34px;
          padding: 0 32px;
          border: 1px solid #e0e3e8;
          border-radius: 7px;
          outline: none;
          font-size: 9px;
        }

        .message-search button {
          position: absolute;
          top: 50%;
          right: 6px;
          transform: translateY(-50%);
          border: 0;
          background: transparent;
          color: #858b95;
          cursor: pointer;
        }

        .toolbar-filters {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .toolbar-filters select {
          min-height: 34px;
          padding: 0 8px;
          border: 1px solid #e0e3e8;
          border-radius: 7px;
          background: #fff;
          color: #555b65;
          font-size: 9px;
        }

        .refresh-report-btn {
          min-height: 34px;
          padding: 0 9px;
          border-radius: 7px;
          background: #f2f3f6;
          color: #5c626d;
          font-size: 9px;
        }

        .message-report-card {
          overflow: hidden;
          border: 1px solid #e3e6eb;
          border-radius: 10px;
          background: #fff;
        }

        .report-card-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 10px 12px;
          border-bottom: 1px solid #eceef2;
        }

        .report-card-head > div {
          display: flex;
          align-items: baseline;
          gap: 6px;
        }

        .report-card-head strong {
          color: #454a54;
          font-size: 11px;
        }

        .report-card-head span {
          color: #949aa4;
          font-size: 8px;
        }

        .message-table-wrap {
          width: 100%;
          overflow-x: auto;
        }

        .message-delivery-table {
          width: 100%;
          min-width: 1180px;
          border-collapse: collapse;
        }

        .message-delivery-table th {
          padding: 8px 7px;
          border-bottom: 1px solid #e6e8ec;
          background: #f7f8fa;
          color: #686f7b;
          font-size: 8px;
          font-weight: 800;
          text-align: center;
          white-space: nowrap;
        }

        .message-delivery-table th:first-child {
          text-align: left;
        }

        .message-delivery-table td {
          padding: 8px 7px;
          border-bottom: 1px solid #eff0f3;
          color: #515761;
          font-size: 9px;
          text-align: center;
          vertical-align: middle;
        }

        .message-delivery-table td:first-child {
          text-align: left;
        }

        .message-delivery-table tbody tr:last-child td {
          border-bottom: 0;
        }

        .message-delivery-table tbody tr:hover {
          background: #fbfbfd;
        }

        .student-info-cell {
          display: flex;
          align-items: center;
          min-width: 185px;
          gap: 7px;
        }

        .student-avatar {
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 32px;
          border-radius: 50%;
          background: #eef0ff;
          color: #696cff;
          font-size: 10px;
          font-weight: 800;
        }

        .student-avatar.large {
          width: 45px;
          height: 45px;
          flex-basis: 45px;
          font-size: 14px;
        }

        .student-info-cell > div {
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .student-info-cell strong {
          overflow: hidden;
          max-width: 180px;
          color: #454a54;
          font-size: 10px;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .student-info-cell small {
          margin-top: 1px;
          color: #9298a3;
          font-size: 8px;
        }

        .attendance-mini {
          display: inline-flex;
          padding: 4px 6px;
          border-radius: 5px;
          font-size: 8px;
          font-weight: 800;
          white-space: nowrap;
        }

        .attendance-mini.present {
          background: #e8f8ef;
          color: #278b53;
        }

        .attendance-mini.absent {
          background: #ffeded;
          color: #d34849;
        }

        .attendance-mini.not-marked {
          background: #fff3e2;
          color: #b9781c;
        }

        .time-cell {
          color: #606671;
          font-size: 9px;
          font-weight: 700;
          white-space: nowrap;
        }

        .delivery-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 3px;
          padding: 4px 6px;
          border-radius: 5px;
          font-size: 8px;
          font-weight: 800;
          line-height: 1.2;
          white-space: nowrap;
        }

        .delivery-sent {
          background: #e8f8ef;
          color: #278b53;
        }

        .delivery-not-sent {
          background: #fff3e2;
          color: #bb761b;
        }

        .delivery-processing {
          background: #eaf6ff;
          color: #347fae;
        }

        .delivery-failed {
          background: #ffeded;
          color: #d34849;
        }

        .table-dash {
          color: #b0b4bc;
        }

        .overall-delivery-cell {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 2px;
        }

        .overall-delivery-cell small {
          max-width: 130px;
          color: #858b96;
          font-size: 7px;
          line-height: 1.25;
        }

        .view-history-btn {
          min-height: 28px;
          padding: 0 7px;
          border-radius: 6px;
          background: #eef0ff;
          color: #696cff;
          font-size: 8px;
        }

        .message-mobile-list {
          display: none;
        }

        .report-no-results {
          min-height: 220px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          padding: 25px;
          color: #989da7;
          text-align: center;
        }

        .report-no-results > i {
          margin-bottom: 5px;
          font-size: 27px;
        }

        .report-no-results strong {
          color: #5b616b;
          font-size: 11px;
        }

        .report-no-results span {
          margin-top: 3px;
          font-size: 8px;
        }

        /* MODAL */
        .delivery-modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
          background: rgba(39, 44, 52, .55);
          backdrop-filter: blur(2px);
        }

        .delivery-history-modal {
          width: 100%;
          max-width: 650px;
          max-height: calc(100vh - 36px);
          overflow: hidden;
          border-radius: 13px;
          background: #fff;
          box-shadow: 0 22px 60px rgba(25, 30, 38, .22);
        }

        .delivery-modal-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
          padding: 14px 16px;
          border-bottom: 1px solid #e7e9ed;
        }

        .delivery-modal-header h4 {
          display: flex;
          align-items: center;
          gap: 6px;
          margin: 0;
          color: #424751;
          font-size: 15px;
        }

        .delivery-modal-header h4 i {
          color: #696cff;
        }

        .delivery-modal-header p {
          margin: 3px 0 0;
          color: #979ca6;
          font-size: 9px;
        }

        .delivery-modal-header > button {
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 0;
          border-radius: 7px;
          background: #f2f3f5;
          color: #68707b;
          cursor: pointer;
          font-size: 17px;
        }

        .delivery-modal-body {
          max-height: calc(100vh - 180px);
          overflow-y: auto;
          padding: 14px 16px;
        }

        .modal-student-card {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 10px;
          border: 1px solid #e3e6eb;
          border-radius: 9px;
          background: #fafbfc;
        }

        .modal-student-card > div {
          min-width: 0;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .modal-student-card > div strong {
          color: #444a54;
          font-size: 12px;
        }

        .modal-student-card > div small {
          margin-top: 2px;
          color: #959ba5;
          font-size: 8px;
        }

        .delivered-via-box {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 9px 10px;
          margin-top: 9px;
          border: 1px solid #cfe8d9;
          border-radius: 8px;
          background: #eef9f2;
          color: #278754;
        }

        .delivered-via-box > i {
          font-size: 19px;
        }

        .delivered-via-box > div {
          display: flex;
          flex-direction: column;
        }

        .delivered-via-box small {
          font-size: 8px;
        }

        .delivered-via-box strong {
          margin-top: 1px;
          font-size: 10px;
        }

        .history-title {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          margin: 14px 0 7px;
        }

        .history-title strong {
          color: #4a505a;
          font-size: 10px;
        }

        .history-title span {
          color: #969ca6;
          font-size: 8px;
        }

        .history-event-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .event-channel-card {
          overflow: hidden;
          border: 1px solid #e1e4e9;
          border-radius: 9px;
        }

        .event-channel-title {
          display: flex;
          align-items: center;
          gap: 7px;
          padding: 8px 10px;
          border-bottom: 1px solid #eceef2;
          background: #fafbfc;
        }

        .event-channel-title > span {
          width: 29px;
          height: 29px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 7px;
          background: #eef0ff;
          color: #696cff;
          font-size: 14px;
        }

        .event-channel-title > div {
          display: flex;
          flex-direction: column;
        }

        .event-channel-title strong {
          color: #484e58;
          font-size: 10px;
        }

        .event-channel-title small {
          color: #969ca6;
          font-size: 8px;
        }

        .event-channel-statuses {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
          padding: 9px 10px;
        }

        .event-channel-statuses > div {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 6px;
          min-width: 0;
          padding: 7px;
          border: 1px solid #e7e9ed;
          border-radius: 7px;
        }

        .event-channel-statuses small {
          overflow: hidden;
          color: #777e89;
          font-size: 8px;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .event-delivered-via {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 6px 10px;
          border-top: 1px solid #dcefe4;
          background: #eef9f2;
          color: #278754;
          font-size: 8px;
        }

        .history-empty {
          min-height: 130px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          padding: 20px;
          border: 1px dashed #d8dbe2;
          border-radius: 8px;
          color: #959ba5;
          text-align: center;
        }

        .history-empty i {
          margin-bottom: 5px;
          font-size: 25px;
        }

        .history-empty strong {
          color: #5d636d;
          font-size: 10px;
        }

        .history-empty span {
          max-width: 330px;
          margin-top: 3px;
          font-size: 8px;
          line-height: 1.5;
        }

        .delivery-modal-footer {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding: 10px 16px;
          border-top: 1px solid #e7e9ed;
        }

        .delivery-modal-footer button {
          min-height: 34px;
          padding: 0 12px;
          border: 1px solid #dce0e6;
          border-radius: 7px;
          background: #fff;
          color: #5d6470;
          cursor: pointer;
          font-size: 9px;
          font-weight: 700;
        }

        @media (max-width: 1199px) {
          .message-filter-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }

          .delivery-summary-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }

        @media (max-width: 767px) {
          .message-report-page {
            padding-left: 9px !important;
            padding-right: 9px !important;
          }

          .message-page-header {
            align-items: flex-start;
            flex-direction: column;
            padding: 12px;
          }

          .page-header-actions {
            width: 100%;
          }

          .report-action-btn {
            flex: 1;
          }

          .message-filter-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .delivery-summary-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .message-report-toolbar {
            align-items: stretch;
            flex-direction: column;
          }

          .message-search {
            max-width: none;
          }

          .toolbar-filters {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .refresh-report-btn {
            grid-column: 1 / -1;
          }

          .message-table-wrap {
            display: none;
          }

          .message-mobile-list {
            display: flex;
            flex-direction: column;
          }

          .message-mobile-card {
            width: 100%;
            padding: 10px;
            border: 0;
            border-bottom: 1px solid #eceef2;
            background: #fff;
            text-align: left;
            cursor: pointer;
          }

          .message-mobile-card:last-child {
            border-bottom: 0;
          }

          .mobile-card-top {
            display: flex;
            align-items: center;
            gap: 7px;
          }

          .mobile-card-top > div {
            min-width: 0;
            flex: 1;
            display: flex;
            flex-direction: column;
          }

          .mobile-card-top > div strong {
            overflow: hidden;
            color: #464b55;
            font-size: 10px;
            white-space: nowrap;
            text-overflow: ellipsis;
          }

          .mobile-card-top > div small {
            color: #949aa4;
            font-size: 8px;
          }

          .mobile-card-info {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 5px;
            margin-top: 8px;
          }

          .mobile-card-info span {
            display: flex;
            flex-direction: column;
            padding: 6px;
            border-radius: 6px;
            background: #f7f8fa;
            color: #979ca6;
            font-size: 7px;
          }

          .mobile-card-info strong {
            margin-top: 2px;
            color: #555b65;
            font-size: 8px;
            line-height: 1.25;
          }
        }

        @media (max-width: 480px) {
          .message-filter-grid {
            grid-template-columns: 1fr;
          }

          .delivery-summary-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .toolbar-filters {
            grid-template-columns: 1fr;
          }

          .refresh-report-btn {
            grid-column: auto;
          }

          .mobile-card-info {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .event-channel-statuses {
            grid-template-columns: 1fr;
          }

          .modal-student-card {
            align-items: flex-start;
            flex-wrap: wrap;
          }

          .modal-student-card > .delivery-badge {
            margin-left: 54px;
          }
        }
      
        /* =====================================================
           APPLICATION SHELL / ATTENDANCE V2
        ===================================================== */
        .attendance-dashboard {
          min-height: 100vh;
          background: #f6f7fb;
        }

        .attendance-dashboard .layout-container {
          min-height: 100vh;
        }

        .message-report-layout {
          min-width: 0;
          width: 100%;
          min-height: 100vh;
        }

        .attendance-dashboard .content-wrapper {
          min-width: 0;
          width: 100%;
        }

        .attendance-dashboard .container-p-y {
          width: 100%;
          max-width: 100%;
          min-width: 0;
        }

        .message-master-v2-wrapper {
          width: 100%;
          min-width: 0;
          margin-bottom: 16px;
        }

        .message-report-page {
          width: 100%;
          max-width: 1750px;
          min-width: 0;
          margin: 0 auto;
        }

        @media (max-width: 991.98px) {
          .message-master-v2-wrapper {
            margin-bottom: 12px;
          }
        }

        @media (max-width: 575.98px) {
          .message-master-v2-wrapper {
            margin-bottom: 10px;
          }
        }

        @media print {
          .attendance-dashboard .no-print,
          .message-master-v2-wrapper {
            display: none !important;
          }
        }

`}</style>

      {/* Attendance-only layout fix: remove fixed-template navbar offset. */}
      <style>{`
        html.layout-navbar-fixed .attendance-dashboard.layout-wrapper,
        html.layout-navbar-fixed .attendance-dashboard.layout-wrapper > .layout-container,
        .attendance-dashboard.layout-wrapper > .layout-container {
          padding-top: 0 !important;
          margin-top: 0 !important;
        }
        html.layout-navbar-fixed .attendance-dashboard.layout-wrapper .layout-page,
        html.layout-navbar-fixed .layout-wrapper:not(.layout-without-menu).attendance-dashboard .layout-page,
        .attendance-dashboard.layout-wrapper .layout-page {
          padding-top: 0 !important;
          margin-top: 0 !important;
          min-width: 0;
        }
        .attendance-dashboard.layout-wrapper #layout-navbar.scai-topbar,
        html.layout-navbar-fixed .attendance-dashboard.layout-wrapper #layout-navbar {
          top: 0 !important;
          left: auto !important;
          right: auto !important;
          width: 100% !important;
          max-width: none !important;
          margin: 0 !important;
          padding: 0 !important;
          border-radius: 0 !important;
        }
        .attendance-resend-type {
          height: 28px !important;
          min-height: 28px !important;
          max-width: 100px;
          padding: 2px 22px 2px 7px !important;
          font-size: 10px !important;
          border-radius: 6px;
        }
        .attendance-resend-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
          min-height: 28px;
          height: 28px;
          padding: 4px 9px;
          border: 0;
          border-radius: 6px;
          background: #2563eb;
          color: #fff;
          font-size: 10px;
          line-height: 1;
          white-space: nowrap;
          cursor: pointer;
        }
        .attendance-resend-btn:disabled {
          opacity: .65;
          cursor: not-allowed;
        }
        .attendance-resend-btn .spinner-border {
          width: 11px;
          height: 11px;
          border-width: 2px;
        }
        @media print {
          .attendance-dashboard.layout-wrapper #layout-navbar { display: none !important; }
        }
      `}</style>
    </>
  );
}
